import { memo, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

import { scrollTo } from 'utils';
import MoreItemsDropdown from 'widgets/clusterHeader/common/MoreItemsDropdown.jsx';

import subHeaderStyles from '../../css/subHeader.module.css';
import classes from './index.module.css';

const propTypes = {
  navSectionItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired, // without #
    })
  ).isRequired,
};

const InPageNavigation = ({ navSectionItems }) => {
  const router = useRouter();

  const [leftArrow, setLeftArrow] = useState(false);
  const [rightArrow, setRightArrow] = useState(true);

  const containerRef = useRef();

  useEffect(() => {
    checkArrows();
  }, []);

  function checkArrows() {
    if (containerRef.current) {
      const maxScrollLeft = containerRef.current.scrollWidth - containerRef.current.clientWidth;
      setLeftArrow(containerRef.current.scrollLeft > 0);
      setRightArrow(
        maxScrollLeft > 1 ? !(maxScrollLeft < containerRef.current.scrollLeft + 1) : false
      );
    }
  }

  /**
   *
   * @param {Object} element Container Tab Element
   * @param {Boolean} right Whether to scroll right or left
   */
  function handleSlideSubmenu(element, right) {
    const sl = element.current.scrollLeft;
    const cw = element.current.scrollWidth;
    const increment = 300;

    if (right) {
      if (sl + increment >= cw) {
        element.current.scrollTo(cw, 0);
      } else {
        element.current.scrollTo(sl + increment, 0);
      }
    } else {
      if (sl - increment <= 0) {
        element.current.scrollTo(0, 0);
      } else {
        element.current.scrollTo(sl - increment, 0);
      }
    }
    checkArrows();
  }

  const { navItems = [], moreNavItems = [] } = useMemo(() => {
    const totalLength = navSectionItems.length;

    /*
      algorithm that calculates the number of displayedNav (not in "More") based on the total text length
    */
    if (totalLength > 5) {
      let loopCount = 0;
      const MAX_LOOP = 20; // just in case variable to prevent infinite loop

      // check length of the first 5 nav items;
      let displayedNavCount = 5; // what gets shown, ////////// else if not within navSectionItems.slice(0, displayedNavCount) it will be shown in "More"

      // we only get 28 characters (for width) since more than that will introduce a line break... basically the width can only fit 28 characters
      let navItemsTextLength = navSectionItems
        .slice(0, displayedNavCount)
        .map((nav) => nav.title.slice(0, 28))
        .join('').length;

      // depending on the length of the first 5 nav items, we determine whether we need to add more through its length
      while (navItemsTextLength < 70) {
        loopCount++;
        const nextNavItem = navSectionItems[displayedNavCount];

        // prevent infinite loop if undefined or 0
        if (!nextNavItem?.title?.length) {
          break;
        }

        navItemsTextLength += nextNavItem.title.length;
        displayedNavCount++;

        // just in case to prevent infinite loop
        if (loopCount >= MAX_LOOP) {
          break;
        }
      }

      return {
        navItems: navSectionItems.slice(0, displayedNavCount),
        moreNavItems: navSectionItems.slice(displayedNavCount, navSectionItems.length),
      };
    }
    return { navItems: navSectionItems, moreNavItems: [] };
  }, [navSectionItems]);

  const onClickNav = (e, item) => {
    e.preventDefault();
    return scrollTo(`#${item.id}`, 700);
  };

  return (
    <div id="in-page-nav" className="container">
      <div
        className={classNames(
          classes.inPageNavigationContainer,
          'w-100 mt-5 d-xl-flex justify-content-between'
        )}
      >
        <div>
          <div
            className={classNames(classes.onThisPageContainer, 'd-flex flex-row flex-xl-column')}
          >
            <p className="mr-2 m-xl-0 openSansBlackBold">On This Page</p>
            <p className={classNames(classes.skipToSectionText, 'm-0')}>Skip to section</p>
          </div>
        </div>
        <div
          ref={containerRef}
          className={classNames(
            classes.inPageNavigationSectionContainer,
            'd-flex align-items-center'
          )}
          onScroll={checkArrows}
        >
          {leftArrow && (
            <div
              onClick={() => handleSlideSubmenu(containerRef)}
              className={classNames(
                'd-flex align-items-center justify-content-center d-xl-none',
                classes.arrow,
                classes.lArrow
              )}
            >
              <i aria-hidden="true" />
            </div>
          )}

          <div className="d-flex align-items-center">
            {navItems.map((navItem) => (
              <a
                key={`inpagenav${navItem.id}`}
                href={`${router.asPath}#${navItem.id}`}
                onClick={(e) => onClickNav(e, navItem)}
                className={classNames(
                  classes.inPageNavigationSection,
                  'open-sans-medium-black mr-4'
                )}
              >
                {navItem.title}
              </a>
            ))}

            {!!moreNavItems.length && (
              <>
                <div className="d-none d-xl-block">
                  <MoreItemsDropdown
                    id="in-page-more-items-dropdown"
                    items={moreNavItems.map((moreNavItem) => ({
                      ...moreNavItem,
                      link: `${router.asPath}#${moreNavItem.id}`,
                    }))}
                    onClick={onClickNav}
                  />
                </div>
                {moreNavItems.map((navItem) => (
                  <a
                    key={`inpagenav${navItem.id}`}
                    href={`${router.asPath}#${navItem.id}`}
                    onClick={(e) => onClickNav(e, navItem)}
                    className={classNames(
                      classes.inPageNavigationSection,
                      'open-sans-medium-black mr-4 d-inline-block d-xl-none'
                    )}
                  >
                    {navItem.title}
                  </a>
                ))}
              </>
            )}
          </div>

          {rightArrow && (
            <div
              onClick={() => handleSlideSubmenu(containerRef, true)}
              className={classNames(
                'd-flex align-items-center justify-content-center d-xl-none',
                classes.arrow,
                classes.rArrow
              )}
            >
              <i aria-hidden="true" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

InPageNavigation.propTypes = propTypes;

export default memo(InPageNavigation);
